import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import {
  ApplicantFilter,
  Application
} from '../../shared/models/application/application.model';
import { Job, JobPromotion, JobRequirement } from './job.model';
import { environment } from '../../../environments/environment';
import * as ModalActions from '../../ngrx/modal/actions/modal.action';
import { Store } from '@ngrx/store';
import { IAppState } from '../../ngrx';
import { ToastComponent } from '../../ui/toast/toast.component';
import { Qualification } from '../../shared/models/qualification/qualification.model';
import { TLangCode } from '../../ngrx/locale/states/locale.state';
import { PromotionStatus } from '../../promotion/interfaces/promotion-provider.interface';
import { FinnService } from '../../finn/services/finn.service';
import { AiScoringStatusParams } from '../ai/ai.model';



@Injectable({
  providedIn: 'root',
})
export class JobService {
  private apiUrl = environment.apiUrl;
  private jobsUrl = this.apiUrl + 'jobs/';

  store = inject(Store<IAppState>);
  http = inject(HttpClient);
  finnService = inject(FinnService);
  translate = inject(TranslateService);

  public getJob(id: number): Observable<Job> {
    return this.http.get<Job>(this.jobsUrl + id + '/');
  }

  public getJobApplications(jobId: number, filters?: ApplicantFilter): Observable<Application[]> {
    let params = new HttpParams();

    if (filters?.tags?.length) {
      params = params.append('tags', filters.tags.join(','));
    }

    if (filters?.answers?.length) {
      params = params.append('answers', filters.answers.join(','));
    }

    if (filters?.questions?.length) {
      params = params.append('questions', filters.questions.join(','));
    }

    if (filters?.rating?.length) {
      params = params.append('rating', filters.rating.join(','));
    }

    return this.http.get<Application[]>(this.jobsUrl + jobId + '/applications/', { params });
  }

  public deleteJob(jobId): Observable<any> {
    return this.http.delete(this.jobsUrl + jobId + '/').pipe(
      catchError((err: HttpErrorResponse) => {
        const message: string = err && err.error && err.error.toLowerCase().includes('deadlock') ?
                                'DEADLOCK_ERROR' : 'DELETE_JOB_ERROR';

        this.store.dispatch(
          ModalActions.open({
            cmpType: ToastComponent,
            props: {
              view: 'error',
              title: this.translate.instant(message)
            },
          }));

        throw err;
      })
    );
  }

  public updateJob(job): Observable<Job> {
    return this.http.put<Job>(this.jobsUrl + job.id + '/', job);
  }

  public patchJob(jobId: number, job: Partial<Job>): Observable<Job> {
    return this.http.patch<Job>(this.jobsUrl + jobId + '/', job);
  }

  getJobMailTemplates(jobId: number): Observable<any> {
    return this.http.get(this.jobsUrl + jobId + '/mail-template/');
  }

  createJobMailTemplates(jobId: number, data): Observable<any> {
    return this.http.post(this.jobsUrl + jobId + '/create-mail-template/', data);
  }

  allowUsageOfSpecificTemplates(jobId: number, data: boolean): Observable<any> {
    return this.http.post(this.jobsUrl + jobId + '/mail-template/', data);
  }

  updateJobMailTemplates(jobId: number, newData): Observable<any> {
    return this.http.patch(this.jobsUrl + jobId + '/mail-template/', newData);
  }

  updateJobEmployee(jobId: number, employeeId: number, employeeData): Observable<any> {
    return this.http.patch(this.jobsUrl + jobId + '/employees/' + employeeId, employeeData);
  }

  updateAdvancedJob(jobId: number, advancedData): Observable<any> {
    return this.http.patch(this.jobsUrl + jobId + '/advanced/', advancedData);
  }

  public getJobDocsName(jobRequirements: JobRequirement[], languageCode?: TLangCode): string {
    return jobRequirements?.find(r => r.name === 'otherFile')?.translations?.[languageCode]?.name;
  }

  deleteJobApplications(jobId: number, ids: number[]): Observable<any> {
    const body = {
      applications_ids: ids
    };

    return this.http.request('delete', `${this.jobsUrl}${jobId}/applications/actions/`, { body });
  }

  /**
   * By default job duration is the same as Finn max duration - 42 days
   */
  getDefaultExpirationDate(): any {
    return this.finnService.getFinnMaxPossible();
  }

  /**
   * job question is considered as invalid if:
   * it is multiple choice type and has at least one empty answer
   */
  isJobQuestionsInvalid(questions: Qualification[]): boolean {
    if (!questions || !questions.length) {
      return false;
    }
  }

  removeInvalidJobQuestions(questions: Qualification[]): Qualification[] {
    if (!questions) {
      return;
    }
  }

  public setJobLanguages(id: number, languages: any): Observable<Job> {
    return this.http.put<Job>(`${this.jobsUrl}${id}/languages/`, languages);
  }

  getJobPromotions(jobId: number, filters?: {
    status?: PromotionStatus[];
  }): Observable<JobPromotion[]> {
    const statuses = filters?.status?.length ? filters.status.join(',') : null;
    let params = new HttpParams();

    if (statuses) {
      params = params.append('status', statuses);
    }

    return this.http.get<JobPromotion[]>(`${this.jobsUrl}${jobId}/promotions/`, { params });
  }

  setAiScoringStatus(jobId: number, data: AiScoringStatusParams): Observable<AiScoringStatusParams> {
    return this.http.put<AiScoringStatusParams>(`${this.jobsUrl}${jobId}/ai/scoring/`, data);
  }
}
